import { useEffect, useState } from "react";
import { Anchor, ArrowRight, CloudRain, Navigation, Phone, Mail, Radio, Waves, Wind } from "lucide-react";
import { T } from "./landingText";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://www.portoturisticodiroma.it";
const BOOK = `${SITE}/prenota-il-tuo-ormeggio/`;
const dir = (d) => ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(((d || 0) % 360) / 45) % 8];
const Kicker = ({ children }) => <p className="text-xs font-bold uppercase tracking-[0.25em] text-[var(--teal)]">{children}</p>;

function LangSwitch({ lang, setLang }) {
  return (
    <div className="flex rounded-full bg-white/15 backdrop-blur p-1 text-xs font-bold" data-testid="landing-lang-switch">
      {["it", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-full uppercase transition-colors ${lang === l ? "bg-white text-[var(--navy)]" : "text-white hover:bg-white/10"}`} data-testid={`landing-lang-${l}`}>{l}</button>
      ))}
    </div>
  );
}

function Hero({ t, lang, setLang }) {
  return (
    <header className="relative min-h-[92vh] flex flex-col text-white overflow-hidden" data-testid="landing-hero">
      <img src="/landing/about2.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1b33] via-[#0b1b33]/55 to-[#0b1b33]/30" />
      <nav className="relative z-10 flex items-center justify-between px-5 sm:px-10 pt-10 pb-5">
        <img src="/logo_white.png" alt="Porto Turistico di Roma" className="h-10 sm:h-12" data-testid="landing-logo" />
        <div className="flex items-center gap-3">
          <LangSwitch lang={lang} setLang={setLang} />
        </div>
      </nav>
      <div className="relative z-10 mt-auto px-5 sm:px-10 pb-14 max-w-4xl">
        <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-white/80 mb-4">{t.kicker}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">{t.title}</h1>
        <p className="mt-5 text-base sm:text-lg text-white/85 max-w-2xl">{t.lead}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#ormeggio" className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-6 py-3 font-bold hover:brightness-110 transition-[filter]" data-testid="landing-book-hero"><Anchor className="w-4 h-4" />{t.book}</a>
        </div>
      </div>
      <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 border-t border-white/15 bg-[#0b1b33]/70 backdrop-blur" data-testid="landing-stats">
        {t.stats.map(([n, l]) => <div key={l} className="px-5 sm:px-10 py-5 border-white/10 sm:border-r last:border-0"><p className="font-display text-2xl sm:text-3xl font-bold">{n}</p><p className="text-xs uppercase tracking-wider text-white/70">{l}</p></div>)}
      </div>
    </header>
  );
}

const About = ({ t }) => (
  <section className="px-5 sm:px-10 py-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center max-w-7xl mx-auto" data-testid="landing-about">
    <div className="space-y-5">
      <Kicker>{t.aboutK}</Kicker>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-[var(--navy)]">{t.aboutT}</h2>
      <p className="text-slate-600 leading-relaxed">{t.about}</p>
      <a href={`${SITE}/il-porto-di-roma/`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-bold text-[var(--teal)] hover:gap-3 transition-[gap]" data-testid="landing-about-link">{t.more}<ArrowRight className="w-4 h-4" /></a>
    </div>
    <div className="grid grid-cols-5 gap-3">
      <img src="/landing/about1.jpg" alt="" className="col-span-3 h-80 w-full object-cover rounded-3xl" />
      <div className="col-span-2 grid gap-3"><img src="/landing/porto.jpg" alt="" className="h-[154px] w-full object-cover rounded-3xl" /><img src="/landing/hero.jpg" alt="" className="h-[154px] w-full object-cover rounded-3xl" /></div>
      <img src="/landing/mappa.jpg" alt="" className="col-span-5 h-40 w-full object-cover rounded-3xl" />
    </div>
  </section>
);

function Weather({ t }) {
  const [w, setW] = useState(undefined);
  useEffect(() => { fetch(`${API}/api/weather/public`).then((r) => (r.ok ? r.json() : null)).then(setW).catch(() => setW(null)); }, []);
  return (
    <section className="bg-[var(--navy)] text-white px-5 sm:px-10 py-20" data-testid="landing-weather">
      <div className="max-w-7xl mx-auto space-y-8">
        <div><Kicker>{t.weatherK}</Kicker><h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">{t.weatherT}</h2></div>
        {w === undefined ? <p className="text-white/60">…</p> : !w ? <p className="text-white/70" data-testid="landing-weather-error">{t.noWeather}</p> : (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-[var(--teal)] p-6 space-y-3" data-testid="landing-weather-now">
              <p className="text-xs uppercase tracking-widest text-white/80">{t.now}</p>
              <p className="flex items-center gap-2 text-2xl font-bold"><Wind className="w-6 h-6" />{w.current.wind_speed_knots} kn <span className="text-sm font-semibold flex items-center gap-1"><Navigation className="w-4 h-4" style={{ transform: `rotate(${w.current.wind_direction_deg + 180}deg)` }} />{dir(w.current.wind_direction_deg)}</span></p>
              <p className="flex items-center gap-2"><Waves className="w-5 h-5" />{t.wave} {w.current.wave_height_m} m</p>
              <p className="flex items-center gap-2"><CloudRain className="w-5 h-5" />{t.rain} {w.current.rain_mm} mm</p>
            </div>
            {w.daily.slice(0, 3).map((d, i) => (
              <div key={d.date} className="rounded-3xl bg-white/5 border border-white/10 p-6 space-y-2" data-testid={`landing-weather-day-${i}`}>
                <p className="text-xs uppercase tracking-widest text-white/60">{t.days[i]} · {d.date.split("-").reverse().slice(0, 2).join("/")}</p>
                <p className="flex items-center gap-2 font-semibold"><Wind className="w-4 h-4 text-[var(--teal)]" />{t.wind} {d.wind_speed_max_knots} kn {dir(d.wind_direction_dominant_deg)}</p>
                <p className="flex items-center gap-2"><Waves className="w-4 h-4 text-[var(--teal)]" />{t.wave} {d.wave_height_max_m} m</p>
                <p className="flex items-center gap-2"><CloudRain className="w-4 h-4 text-[var(--teal)]" />{t.rain} {d.rain_mm} mm</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function News({ t }) {
  const [n, setN] = useState([]);
  useEffect(() => { fetch(`${API}/api/local/news/public`).then((r) => r.json()).then((d) => setN(d.news || [])).catch(() => setN([])); }, []);
  if (!n.length) return null;
  return (
    <section className="px-5 sm:px-10 py-20 max-w-7xl mx-auto space-y-8" data-testid="landing-news">
      <div className="flex flex-wrap items-end justify-between gap-4"><div><Kicker>{t.newsK}</Kicker><h2 className="font-display text-3xl sm:text-4xl font-bold text-[var(--navy)] mt-2">{t.newsT}</h2></div>
        <a href={`${SITE}/news`} target="_blank" rel="noopener noreferrer" className="font-bold text-[var(--teal)] inline-flex items-center gap-2" data-testid="landing-news-all">{t.allNews}<ArrowRight className="w-4 h-4" /></a></div>
      <div className="grid md:grid-cols-3 gap-6">
        {n.map((x, i) => (
          <a key={x.url} href={x.url} target="_blank" rel="noopener noreferrer" className="group rounded-3xl overflow-hidden bg-white ring-1 ring-slate-200 hover:-translate-y-1 transition-transform" data-testid={`landing-news-${i}`}>
            {x.image && <img src={x.image} alt="" className="h-48 w-full object-cover" />}
            <div className="p-6 space-y-2"><p className="text-xs uppercase tracking-wider text-slate-400">{x.data}</p><h3 className="font-display text-lg font-bold text-[var(--navy)] leading-snug">{x.title}</h3>
              <p className="text-sm text-slate-600 line-clamp-3">{x.excerpt}</p><p className="text-sm font-bold text-[var(--teal)] inline-flex items-center gap-1 group-hover:gap-2 transition-[gap]">{t.read}<ArrowRight className="w-4 h-4" /></p></div>
          </a>
        ))}
      </div>
    </section>
  );
}

const Booking = ({ t }) => (
  <section id="ormeggio" className="px-5 sm:px-10 pb-20 max-w-7xl mx-auto" data-testid="landing-booking">
    <div className="relative rounded-[2rem] overflow-hidden text-white">
      <img src="/landing/porto.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0b1b33]/80" />
      <div className="relative p-8 sm:p-14 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
        <div className="space-y-4"><Kicker>{t.bookK}</Kicker><h2 className="font-display text-3xl sm:text-4xl font-bold">{t.bookT}</h2><p className="text-white/85 leading-relaxed">{t.bookText}</p>
          <a href={BOOK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-6 py-3 font-bold hover:brightness-110 transition-[filter]" data-testid="landing-book-button"><Anchor className="w-4 h-4" />{t.bookBtn}</a></div>
        <div className="space-y-3 text-sm">
          <p className="flex items-center gap-3 rounded-2xl bg-white/10 p-4"><Radio className="w-5 h-5 text-[var(--teal)]" />{t.vhf}</p>
          <a href="tel:+390656188236" className="flex items-center gap-3 rounded-2xl bg-white/10 p-4 hover:bg-white/20 transition-colors" data-testid="landing-phone"><Phone className="w-5 h-5 text-[var(--teal)]" />+39 06 56188236 · {t.office}</a>
          <a href="mailto:direzione.porto@portodiroma.com" className="flex items-center gap-3 rounded-2xl bg-white/10 p-4 hover:bg-white/20 transition-colors [overflow-wrap:anywhere]" data-testid="landing-email"><Mail className="w-5 h-5 text-[var(--teal)]" />direzione.porto@portodiroma.com</a>
        </div>
      </div>
    </div>
  </section>
);

export default function Landing() {
  const [lang, setLang] = useState(() => localStorage.getItem("landing_lang") || "it");
  const t = T[lang];
  useEffect(() => { localStorage.setItem("landing_lang", lang); document.documentElement.lang = lang; }, [lang]);
  return (
    <div className="bg-[#f6f3ec] min-h-screen" data-testid="landing-page">
      <div className="fixed top-0 inset-x-0 z-50 bg-amber-400 text-[#0b1b33] text-center text-xs font-bold py-1" data-testid="landing-preview-banner">{t.preview}</div>
      <Hero t={t} lang={lang} setLang={setLang} />
      <About t={t} />
      <Weather t={t} />
      <News t={t} />
      <Booking t={t} />
      <footer className="bg-[#0b1b33] text-white/60 text-xs px-5 sm:px-10 py-8 flex flex-wrap justify-between gap-3">
        <span>Porto Turistico di Roma · Lungomare Duca degli Abruzzi 84 · 00121 Roma</span><span>Copyright © 2026 Porto Turistico di Roma Srl · P.IVA 08271571005</span>
      </footer>
    </div>
  );
}
